import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subYears} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import mobileService from "../../../services/web/mobile-service.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subYears(new Date(), 1),
    dtEnd: new Date(),
    dispatch: {label: "Todos", value: 'all'},
    source: null,
    bo: {label: "Todos", value: 'all'},
    boNumber: '',
    otherOccurrences: [],
    guard: [],
    garrison: '',
    animals: [],
    hasScheduleOnly: false,
    showDisplacement: false,
    reason: [],
    group_reason: [],
    street: [],
    place: [],
    type_place: [],
    neighborhood: [],
    sector: [],
    team: []
});

const DispatchSequence = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        protocol: {label: "Protocolo", visible: true},
        dispatch: {label: "Despacho", visible: true},
        bo: {label: "BO", visible: true},
        dateTime: {label: "D/H", visible: true},
        nature: {label: "Natureza", visible: true},
        source: {label: "Origem", visible: true},
        providence: {label: "Providência", visible: true},
        address: {label: "Endereço", visible: true},
        garrison: {label: "Guarnição", visible: true},
        initialObservation: {label: "Obs. inicial", visible: true},
        finalObservation: {label: "Obs. final", visible: true}
    }));

    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);

    const buttonRef = useRef(null);

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    const { data: hasMobile } = useQuery({
        queryKey: ['hasMobile'],
        queryFn: () => mobileService.hasMobile(),
        initialData: ''
    });

    const title = `Sequência de despachos`;
    const [headerSelected, setHeaderSelected] = useState(-1);

    const filterLabels = {
        dtStart: "Data/hora inicial",
        dtEnd: "Data/hora final",
        dispatch: "Despacho",
        source: "Origem",
        bo: `${boName}`,
        boNumber: `${boName} N°`,
        otherOccurrences: `${boName} - Documentos`,
        guard: "Contingente",
        garrison: "Guarnição",
        animals: "Animais",
        hasScheduleOnly: "Somente atividades programadas",
        showDisplacement: "Exibir deslocamento",
        reason: "Natureza",
        group_reason: "Grupo de natureza",
        street: "Logradouro",
        place: "Local pré-cadastrado",
        type_place: "Tipo de local",
        neighborhood: "Bairro",
        sector: "Setor",
        team: "Equipe"
    };

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, 'year');

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                dispatch: filters.dispatch.value ?? 'all',
                source: filters.source?.value ?? null,
                bo: filters.bo.value ?? null,
                boNumber: filters.boNumber ?? null,
                otherOccurrences: filters.otherOccurrences?.map(i => i.value) ?? [],
                guard: filters.guard?.map(i => i.value) ?? [],
                garrison: filters.garrison ?? '',
                animals: filters.animals?.map(i => i.value) ?? [],
                hasScheduleOnly: filters.hasScheduleOnly ?? false,
                showDisplacement: filters.showDisplacement ?? false,
                reason: filters.reason?.map(i => i.value) ?? [],
                group_reason: filters.group_reason?.map(i => i.value) ?? [],
                street: filters.street?.map(i => i.value) ?? [],
                place: filters.place?.map(i => i.value) ?? [],
                type_place: filters.type_place?.map(i => i.value) ?? [],
                neighborhood: filters.neighborhood?.map(i => i.value) ?? [],
                sector: filters.sector?.map(i => i.value) ?? [],
                team: filters.team?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/dispatch_sequence/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            setReportData(response);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                visibilityColumns={true}
                                columns={tableColumns}
                                setColumns={setTableColumns}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters} boName={boName}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={reportData?.length}/>
                            <Content reportData={reportData} data={reportData?.length} boName={boName} showDisplacement={appliedFilters.showDisplacement} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default DispatchSequence;